<template>
  <div class="container-fluid mt-4 bg-white px-0">
    <chart-header
      :title="$t('flexigo.dashboard.demand')"
      :has-data="!!demandItems.length"
      @filter-change="onFilterChange"
      @download="download"
    />
    <hr />

    <div class="d-flex gap-3 mb-4 px-3 justify-content-center">
      <div v-for="stat in statistics" :key="stat.label" class="stat-box">
        <div class="stat-label">{{ stat.label }}</div>
        <div class="stat-value">{{ stat.value }}%</div>
      </div>
    </div>
    <div class="px-3">
      <div class="d-flex justify-content-between align-items-center mb-3">
        <div class="chart-left-title">{{ $t('flexigo.dashboard.requests') }}</div>
        <div class="chart-right-title">{{ $t('flexigo.dashboard.avgDeviationFromRequestedTime') }}</div>
      </div>
      <highcharts ref="demandChart" :options="chartOptions" />
    </div>
    <excel-downloader ref="excelDownloader" :data="excelData" :file-name="'Demand_Chart'" :sheet-name="'Demand'" />
  </div>
</template>

<script>
import { mapActions, mapGetters } from 'vuex';
import { format } from 'date-fns';
import { Chart } from 'highcharts-vue';
import {
  getXAxisFormatType,
  formatXAxisLabel,
  groupDataByFormatType,
  aggregateGroupedData,
  sortHourlyItemsByWindow,
} from '@/modules/Flexigo/helpers/chartXAxisFormatter';
import buildGapPlotLines from '@/modules/Flexigo/helpers/chartGapPlotLines';
import { exportChartAsPNG } from '@/modules/Flexigo/components/dashboard/otp/utils/chartExport';
import chartTooltipPositioner from '@/modules/Flexigo/helpers/chartTooltipPositioner';
import chartHeader from './ChartHeader.vue';
import ExcelDownloader from './ExcelDownloader.vue';

export default {
  name: 'DemandChart',
  components: {
    chartHeader,
    highcharts: Chart,
    ExcelDownloader,
  },
  data() {
    return {
      selectedDate: 'thisweek',
      filters: {
        startDate: null,
        endDate: null,
        fromHour: null,
        toHour: null,
      },
    };
  },
  computed: {
    ...mapGetters('onDemandDashboard', {
      demandItems: 'demandItems',
    }),
    xAxisFormatType() {
      return getXAxisFormatType(this.filters.startDate, this.filters.endDate, this.selectedDate);
    },
    // Items as the chart/excel/plot-lines all see them: raw for hourly, grouped+aggregated otherwise.
    // Extracted so the three consumers share one source of truth (previously each ran the same
    // grouping block inline).
    processedItems() {
      const items = this.demandItems || [];
      if (items.length === 0) return items;
      const formatType = this.xAxisFormatType;
      // Overnight operation windows wrap next-day early hours under the same operationDay;
      // reorder so 00:00 lands after 23:00 instead of before 20:00.
      if (formatType === 'hourly') {
        return sortHourlyItemsByWindow(items, formatType, this.filters.fromHour, this.filters.toHour);
      }
      const grouped = groupDataByFormatType(items, formatType);
      return Object.values(grouped).map((groupedItems) =>
        aggregateGroupedData(
          groupedItems,
          ['declined', 'timeRanOut', 'noOfferAvailable', 'accepted', 'requests'], // Sum
          ['averageDeviation'], // Average
        ),
      );
    },
    // Dashed vertical markers with "+Nh"/"+Nd" labels where consecutive bars skip
    // over empty buckets (mirrors Shuttle RidershipByRouteChart).
    gapPlotLines() {
      return buildGapPlotLines(this.processedItems, this.xAxisFormatType);
    },
    excelData() {
      const items = this.processedItems;
      const formatType = this.xAxisFormatType;

      // Determine column header based on formatType
      const firstColumnHeader =
        formatType === 'hourly'
          ? this.$t('flexigo.dashboard.hour') || 'Hour'
          : this.$t('flexigo.dashboard.date') || 'Date';

      return items.map((item) => {
        const label = formatXAxisLabel(item, formatType, this.$i18n.locale);
        const totalRequests = item?.requests || 0;
        const accepted = item?.accepted || 0;
        const declined = item?.declined || 0;
        const timeRanOut = item?.timeRanOut || 0;
        const noOffer = item?.noOfferAvailable || 0;
        const unservedRequests = declined + timeRanOut + noOffer;
        const acceptanceRate = totalRequests > 0 ? Math.round((accepted / totalRequests) * 100) : 0;

        return {
          [firstColumnHeader]: label,
          [this.$t('flexigo.dashboard.totalRequests')]: Math.round(totalRequests),
          [this.$t('flexigo.dashboard.acceptedRequests')]: Math.round(accepted),
          [this.$t('flexigo.dashboard.acceptanceRate')]: acceptanceRate,
          [this.$t('flexigo.dashboard.unservedDemand')]: Math.round(unservedRequests),
          [this.$t('flexigo.dashboard.avgDeviationFromRequestedTime')]: Math.round(item?.averageDeviation || 0),
        };
      });
    },
    statistics() {
      return [
        { label: this.$t('flexigo.dashboard.declined'), value: this.declinedPercentage },
        { label: this.$t('flexigo.dashboard.timeRanOut'), value: this.timeRanOutPercentage },
        { label: this.$t('flexigo.dashboard.noOfferAvailable'), value: this.noOfferPercentage },
        { label: this.$t('flexigo.dashboard.totalUnservedDemand'), value: this.totalUnservedPercentage },
      ];
    },
    declinedPercentage() {
      const items = this.demandItems;
      if (!items || items.length === 0) return 0;

      const totalRequests = items.reduce((sum, item) => sum + (item.requests || 0), 0);
      const totalDeclined = items.reduce((sum, item) => sum + (item.declined || 0), 0);

      return totalRequests > 0 ? Math.round((totalDeclined / totalRequests) * 100) : 0;
    },
    timeRanOutPercentage() {
      const items = this.demandItems;
      if (!items || items.length === 0) return 0;

      const totalRequests = items.reduce((sum, item) => sum + (item.requests || 0), 0);
      const totalTimeRanOut = items.reduce((sum, item) => sum + (item.timeRanOut || 0), 0);

      return totalRequests > 0 ? Math.round((totalTimeRanOut / totalRequests) * 100) : 0;
    },
    noOfferPercentage() {
      const items = this.demandItems;
      if (!items || items.length === 0) return 0;

      const totalRequests = items.reduce((sum, item) => sum + (item.requests || 0), 0);
      const totalNoOffer = items.reduce((sum, item) => sum + (item.noOfferAvailable || 0), 0);

      return totalRequests > 0 ? Math.round((totalNoOffer / totalRequests) * 100) : 0;
    },
    totalUnservedPercentage() {
      const items = this.demandItems;
      if (!items || items.length === 0) return 0;

      const totalRequests = items.reduce((sum, item) => sum + (item.requests || 0), 0);
      const totalUnserved = items.reduce((sum, item) => sum + (item.totalUnservedDemand || 0), 0);

      return totalRequests > 0 ? Math.round((totalUnserved / totalRequests) * 100) : 0;
    },
    chartOptions() {
      const vm = this; // Store reference for tooltip
      const chartOptions = {
        chart: {
          type: 'column',
        },
        credits: {
          enabled: false,
        },
        exporting: {
          enabled: false,
        },
        title: {
          text: null,
        },
        xAxis: {
          categories: [],
          crosshair: true,
          // Dashed vertical markers with "+Nh"/"+Nd" labels where consecutive bars skip
          // over empty buckets (mirrors Shuttle RidershipByRouteChart).
          plotLines: this.gapPlotLines,
        },
        yAxis: [
          {
            title: {
              text: '',
            },
            min: 0,
            tickInterval: 3,
            endOnTick: false,
          },
          {
            title: {
              text: '',
            },
            min: 0,
            tickInterval: 3,
            endOnTick: false,
            opposite: true,
            gridLineWidth: 0,
          },
        ],
        plotOptions: {
          column: {
            stacking: 'normal',
            borderWidth: 0,
            pointWidth: 12,
          },
        },
        tooltip: {
          shared: true,
          useHTML: true,
          backgroundColor: '#363647',
          style: { color: '#ffffff' },
          borderRadius: 6,
          positioner(labelWidth, labelHeight, point) {
            return chartTooltipPositioner(labelWidth, labelHeight, point, this.chart);
          },
          formatter() {
            // Get data from points (use series index instead of localized series name)
            const declined = this.points.find((p) => p.series.index === 0)?.y || 0;
            const timeRanOut = this.points.find((p) => p.series.index === 1)?.y || 0;
            const noOffer = this.points.find((p) => p.series.index === 2)?.y || 0;
            const accepted = this.points.find((p) => p.series.index === 3)?.y || 0;
            // Read the deviation off the bucket metrics every point carries, not off the line's
            // own point: a null point (no planned pickups) is left out of a shared tooltip, and so
            // is the whole series when the user hides it in the legend. Only null means N/A.
            const avgDeviation = this.points[0]?.point?.options?.averageDeviation;
            const avgDeviationText = avgDeviation != null ? `${Math.round(avgDeviation)} min` : 'N/A';

            // Get total requests from backend data (stored in point)
            const totalRequests = this.points[0]?.point?.requests || 0;

            // Calculate unserved and acceptance rate
            const unservedRequests = declined + timeRanOut + noOffer;
            const acceptanceRate = totalRequests > 0 ? Math.round((accepted / totalRequests) * 100) : 0;
            const unservedRate = totalRequests > 0 ? Math.round((unservedRequests / totalRequests) * 100) : 0;

            let tooltipHtml = `<div class="dashboard-graph-tooltip"><div class="title">${this.x}</div>`;

            // Total Requests
            tooltipHtml += `
              <div class="d-flex justify-content-between align-items-center mb-1">
                <span class="text-label">${vm.$t('flexigo.dashboard.totalRequests')}</span>
                <span class="text-value">${Math.round(totalRequests)}</span>
              </div>
            `;

            // Accepted Requests
            tooltipHtml += `
              <div class="d-flex justify-content-between align-items-center mb-1">
                <span class="text-label">${vm.$t('flexigo.dashboard.acceptedRequests')}</span>
                <span class="text-value">${Math.round(accepted)}</span>
              </div>
            `;

            // Acceptance Rate
            tooltipHtml += `
              <div class="d-flex justify-content-between align-items-center mb-1">
                <span class="text-label">${vm.$t('flexigo.dashboard.acceptanceRate')}</span>
                <span class="text-value">${acceptanceRate}%</span>
              </div>
            `;

            // Unserved Demand
            tooltipHtml += `
              <div class="d-flex justify-content-between align-items-center mb-1">
                <span class="text-label">${vm.$t('flexigo.dashboard.unservedDemand')}</span>
                <span class="text-value">${unservedRate}%</span>
              </div>
            `;

            // Avg Deviation from Requested Time
            tooltipHtml += `
              <div class="d-flex justify-content-between align-items-center mb-1">
                <span class="text-label">${vm.$t('flexigo.dashboard.avgDeviationFromRequestedTimeShort')}</span>
                <span class="text-value">${avgDeviationText}</span>
              </div>
            `;

            tooltipHtml += '</div>';
            return tooltipHtml;
          },
        },
        legend: {
          squareSymbol: true,
          symbolRadius: 0,
          useHTML: true,
          margin: 30,
          labelFormatter() {
            return `<div style="color: #687692; font-weight: normal;">${this.name}</div>`;
          },
        },
        series: [
          {
            name: this.$t('flexigo.dashboard.declined'),
            data: [],
            color: '#687692',
          },
          {
            name: this.$t('flexigo.dashboard.timeRanOut'),
            data: [],
            color: '#bfc7d1',
          },

          {
            name: this.$t('flexigo.dashboard.noOfferAvailable'),
            data: [],
            color: '#ff8b3d',
          },
          {
            name: this.$t('flexigo.dashboard.acceptedOffersCount'),
            data: [],
            color: '#1cab49',
          },
          {
            name: this.$t('flexigo.dashboard.avgDeviationFromRequestedTime'),
            type: 'spline',
            yAxis: 1,
            data: [],
            color: '#3498db',
            marker: {
              fillColor: '#ffffff',
              lineWidth: 2,
              lineColor: '#3498db',
              radius: 5,
            },
          },
        ],
      };

      const items = this.processedItems;
      const formatType = this.xAxisFormatType;

      // Format X-axis labels
      chartOptions.xAxis.categories = items.map((item) => formatXAxisLabel(item, formatType, this.$i18n.locale));

      // Every point carries the bucket's totals so the shared tooltip can read them off
      // whichever point it lands on. averageDeviation stays null (not 0) when the backend has
      // no deviation for the bucket, i.e. no request received a planned pickup (VSER-14215).
      const bucketMetrics = (item) => ({
        requests: item?.requests || 0,
        averageDeviation: item?.averageDeviation ?? null,
      });

      chartOptions.series[0].data = items.map((item) => ({ y: item?.declined || 0, ...bucketMetrics(item) }));
      chartOptions.series[1].data = items.map((item) => ({ y: item?.timeRanOut || 0, ...bucketMetrics(item) }));
      chartOptions.series[2].data = items.map((item) => ({ y: item?.noOfferAvailable || 0, ...bucketMetrics(item) }));
      chartOptions.series[3].data = items.map((item) => ({ y: item?.accepted || 0, ...bucketMetrics(item) }));
      // The line breaks where the deviation is null instead of dropping to 0.
      chartOptions.series[4].data = items.map((item) => ({
        y: item?.averageDeviation ?? null,
        ...bucketMetrics(item),
      }));

      // Calculate dynamic max for right Y-axis (avg deviation)
      const maxDeviation = Math.max(...items.map((item) => item?.averageDeviation || 0), 0);
      const rightYAxisMax = maxDeviation > 0 ? Math.ceil(maxDeviation * 1.2) : 30; // 20% padding
      chartOptions.yAxis[1].max = rightYAxisMax;
      chartOptions.yAxis[1].tickInterval = Math.ceil(rightYAxisMax / 10);

      return chartOptions;
    },
  },
  methods: {
    ...mapActions('onDemandDashboard', ['fetchDemandData']),

    download(type) {
      if (type === 'image') {
        this.downloadChartAsImage();
        return;
      }
      this.$refs.excelDownloader.download();
    },
    downloadChartAsImage() {
      const chartInstance = this.$refs.demandChart?.chart;

      exportChartAsPNG(chartInstance, `Demand_Chart_${format(new Date(), 'yyyy-MM-dd')}`, {
        width: 1200,
        height: 600,
        scale: 2,
        surface: 'demand',
      });
    },
    async onFilterChange({ startDate, endDate, fromHour, toHour, selectedDate }) {
      this.selectedDate = selectedDate;
      this.filters = { startDate, endDate, fromHour, toHour };

      await this.fetchDemandData({
        startDate,
        endDate,
        fromHour,
        toHour,
      });
    },
  },
};
</script>

<style lang="scss" scoped>
.stat-box {
  border: 1px solid #e6eaee;
  text-align: center;
  display: flex;
  gap: 5px;
  padding: 12px;
  border-radius: 4px;
  font-size: 16px;
}

.stat-label {
  color: #363647;
}

.stat-value {
  font-weight: 600;
  color: #363647;
}

.chart-left-title {
  color: #363647;
}
.chart-right-title {
  color: #5287e3;
}
::v-deep(.dashboard-graph-tooltip) {
  width: 350px !important;
}
</style>
